import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { inputClass, Honeypot, ConsentNote } from "@/components/Shared";
import { postEnquiry } from "@/lib/api";
import { trackEvent, leadContext } from "@/lib/analytics";
import { RESPONSE_PROMISE } from "@/data/content";

const FORM_CONFIGS = {
  general: {
    title: "Send us an enquiry",
    submit: "Send enquiry",
    success: `Thank you — we've received your enquiry and will be in touch ${RESPONSE_PROMISE}.`,
    fields: [
      { name: "name", label: "Name", required: true },
      { name: "email", label: "Email", type: "email", required: true },
      { name: "phone", label: "Phone" },
      { name: "message", label: "How can we help?", type: "textarea", required: true },
    ],
    contactMethod: true,
  },
  booking: {
    title: "Request a consultation",
    submit: "Request booking",
    success: `Thank you — the practice will confirm your consultation ${RESPONSE_PROMISE}. Consultation fees are redeemable against treatment within 30 days.`,
    fields: [
      { name: "name", label: "Name", required: true },
      { name: "email", label: "Email", type: "email", required: true },
      { name: "phone", label: "Phone", required: true },
      {
        name: "consultation_type", label: "Consultation type", type: "select", required: true,
        options: [
          "Physician consultation — any physician, 45 min ($250, redeemable against treatment)",
          "Skin assessment — Dr Kiran Shahid, 45 min ($250, redeemable against treatment)",
          "Hair assessment — Dr Kristy Truong, in person, 45 min ($250, redeemable against treatment)",
          "Body and wellness assessment — Dr Anthony Chaffee, 45 min ($250, redeemable against treatment)",
          "Skin consultation — Lizzie Stock, Skin & Cosmetic Nurse, 45 min ($100, redeemable against treatment)",
          "Review — existing patient, 15 min (no charge)",
          "Not sure yet — please advise",
        ],
      },
      { name: "preferred_date", label: "Preferred date", type: "date" },
      { name: "preferred_time", label: "Preferred time", type: "select", options: ["No preference", "Morning", "Afternoon"] },
      { name: "message", label: "Anything we should know?", type: "textarea" },
    ],
  },
  bridal: {
    title: "Bridal & events enquiry",
    submit: "Send enquiry",
    success: `Thank you — we'll be in touch ${RESPONSE_PROMISE} to start planning backwards from your date.`,
    fields: [
      { name: "name", label: "Name", required: true },
      { name: "email", label: "Email", type: "email", required: true },
      { name: "phone", label: "Phone" },
      { name: "wedding_date", label: "Date of the day", type: "date", required: true },
      { name: "message", label: "Tell us about your day", type: "textarea" },
    ],
  },
  journal: {
    title: "Subscribe to the journal",
    submit: "Subscribe",
    success: "Thank you — you're subscribed. Occasional letters, never noise.",
    fields: [{ name: "email", label: "Email", type: "email", required: true }],
  },
};

export const BOOKING_TYPES = FORM_CONFIGS.booking.fields.find((f) => f.name === "consultation_type").options;

const EnquiryForm = ({ type, heading, defaults }) => {
  const config = FORM_CONFIGS[type];
  const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm({ defaultValues: defaults });
  const [done, setDone] = useState(false);

  const onSubmit = async (data) => {
    try {
      const event = type === "booking" ? "consult_request" : "enquiry";
      const lead = leadContext(event, data.consultation_type);
      await postEnquiry(type, { ...data, meta: lead });
      trackEvent(event, { page: window.location.pathname, form: type, practitioner: data.consultation_type || data.practitioner }, lead);
      setDone(true);
      reset();
      toast.success("Received — thank you.");
    } catch {
      toast.error("Something went wrong. Please try again, or email us directly.");
    }
  };

  if (done) {
    return (
      <div className="bg-cream relative p-10" data-testid={`${type}-form-success`}>
        <div className="gold-frame inset-3 opacity-70" aria-hidden="true" />
        <p className="font-serif font-light text-deep-sea text-2xl relative">Thank you.</p>
        <p className="text-ink/75 text-sm mt-3 leading-relaxed relative">{config.success}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" data-testid={`${type}-form`}>
      <Honeypot register={register} />
      {heading && <h3 className="text-deep-sea mb-2">{heading}</h3>}
      {config.fields.map((field) => (
        <div key={field.name}>
          <label htmlFor={`${type}-${field.name}`} className="eyebrow block mb-2">
            {field.label}{field.required ? "" : " (optional)"}
          </label>
          {field.type === "textarea" ? (
            <textarea
              id={`${type}-${field.name}`}
              rows={4}
              required={field.required}
              placeholder={field.placeholder}
              data-testid={`${type}-${field.name}-input`}
              className={inputClass}
              {...register(field.name, { required: field.required })}
            />
          ) : field.type === "select" ? (
            <select
              id={`${type}-${field.name}`}
              required={field.required}
              data-testid={`${type}-${field.name}-input`}
              className={inputClass}
              {...register(field.name, { required: field.required })}
            >
              <option value="" disabled>Select…</option>
              {field.options.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          ) : (
            <input
              id={`${type}-${field.name}`}
              type={field.type || "text"}
              required={field.required}
              placeholder={field.placeholder}
              data-testid={`${type}-${field.name}-input`}
              className={inputClass}
              {...register(field.name, { required: field.required })}
            />
          )}
        </div>
      ))}
      {config.contactMethod && (
        <div>
          <span className="eyebrow block mb-2">Preferred contact method</span>
          <div className="flex gap-5">
            {["Email", "Phone"].map((m) => (
              <label key={m} className="flex items-center gap-2 text-sm text-ink/85">
                <input type="radio" value={m} defaultChecked={m === "Email"} data-testid={`${type}-contact-${m.toLowerCase()}`} className="accent-[#A68A5B]" {...register("preferred_contact")} />
                {m}
              </label>
            ))}
          </div>
        </div>
      )}
      <label className="flex items-start gap-3 text-sm text-ink/85" data-testid={`${type}-consent-label`}>
        <input type="checkbox" required data-testid={`${type}-consent-checkbox`} className="mt-1 accent-[#A68A5B]" {...register("consent", { required: true })} />
        I consent to Lumière Aesthetics contacting me about my enquiry.
      </label>
      <ConsentNote />
      <button type="submit" disabled={isSubmitting} data-testid={`${type}-submit-button`} className="btn-gold px-8 py-4 text-sm font-medium tracking-[0.06em] disabled:opacity-60">
        {isSubmitting ? "Sending…" : config.submit}
      </button>
    </form>
  );
};

export default EnquiryForm;
